import { useState, useCallback } from 'react';
import { enqueueSnackbar } from 'notistack';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import { useDialog } from '@setvi/shared/providers';
import { useQuoteStore } from 'pages/quotes/store';
import {
  DBEventType,
  useDatabricksAnalytics,
  useFilestack
} from '@setvi/shared/hooks';
import { useAppContext } from 'Providers/AppProvider/AppContext';
import type { UpdateUserDetailsInput } from 'Types/api/globalTypes';
import type { QuoteDTO } from '@setvi/shared/services';
import { TabType } from '../steps/prepared-by';
import type { UserData } from '../steps/prepared-by';
import { QUOTE_DETAILS_DIALOG } from '../types';
import type { Step, QuoteDetailsFormData } from '../types';
import type { useQuoteDetailsMutations } from './use-quote-details-mutations';

interface UseQuoteDetailsSaveParams {
  quote: QuoteDTO;
  formData: QuoteDetailsFormData;
  currentPreparedData: UserData;
  steps: Step[];
  setShowWarning: (val: boolean) => void;
  setIsExpired: (val: boolean) => void;
  mutations: ReturnType<typeof useQuoteDetailsMutations>;
  onClose: () => void;
  updateQuoteInfo: (quote: Partial<QuoteDTO>) => void;
}

export const useQuoteDetailsSave = ({
  quote,
  formData,
  currentPreparedData,
  steps,
  setShowWarning,
  setIsExpired,
  mutations,
  onClose,
  updateQuoteInfo
}: UseQuoteDetailsSaveParams) => {
  const { id: rfqId } = useParams();
  const { closeDialog } = useDialog();
  const { user, setUser } = useAppContext();
  const { setAutoSaveEnabled } = useQuoteStore();
  const { captureDBAnalytics } = useDatabricksAnalytics();
  const { fileStackUpload } = useFilestack();

  const [mainLoading, setMainLoading] = useState(false);

  const updateUser = useCallback(
    async (userData: UserData, file?: File) => {
      const userBody: UpdateUserDetailsInput = {
        UploadId: '',
        Avatar: '',
        FirstName: userData?.firstName || '',
        LastName: userData?.lastName || '',
        Email: userData?.email,
        ContactNumber: userData?.contactNumber || ''
      };

      if (file) {
        const filestackRes = await fileStackUpload({
          file
        });

        if (filestackRes.url) {
          userBody.UploadId = filestackRes.handle;
          userBody.Avatar = filestackRes.url;
        }
      }

      const { Data } = await mutations.updateUserDetails({
        body: userBody
      });
      if (!Data) return;

      const updatedUser = {
        ...user,
        ...Data,
        Photo: userBody.Avatar,
        HasPhoto: !!userBody.Avatar
      };
      setUser(updatedUser);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleSave = useCallback(async () => {
    setMainLoading(true);
    const { preparedBy: preparedByData, preparedFor } = formData;
    const hasIncompleted = steps?.some(s => !s?.completed);

    if (hasIncompleted) {
      setShowWarning(true);
      setMainLoading(false);
      return;
    }

    setAutoSaveEnabled?.(false);

    let updatedPhoto = currentPreparedData?.photo;

    if (preparedByData?.file) {
      try {
        const res = await mutations.updatePhoto({
          id: quote?._id,
          image: preparedByData?.file
        });

        if (!res) return;

        updatedPhoto = res?.previewInfo?.preparedBy?.photo || '';
      } catch (error) {
        enqueueSnackbar('Failed to update sales rep photo', {
          variant: 'error',
          autoHideDuration: 8000
        });
      }
    }

    if (preparedByData?.type === TabType.PROFILE) {
      await updateUser(
        { ...currentPreparedData, photo: updatedPhoto },
        preparedByData?.file
      );
    }

    const fullName =
      `${currentPreparedData.firstName} ${currentPreparedData.lastName}`.trim();

    const { showManufacturerDetails } = formData.quoteDisplay;

    const body = {
      previewInfo: {
        showManufacturerDetails,
        preparedFor: {
          companyPhoneNumber: preparedFor.phoneNumber,
          companyAddress: preparedFor.address,
          companyName: preparedFor?.companyName
        },
        preparedBy: {
          email: currentPreparedData?.email,
          phoneNumber: currentPreparedData?.contactNumber,
          fullName,
          photo: updatedPhoto,
          companyRole: preparedByData?.companyRole,
          firstName: currentPreparedData?.firstName,
          lastName: currentPreparedData?.lastName,
          usedForOtherSalesRep: preparedByData.type === TabType.OTHER
        }
      }
    };

    const mainBody = {
      validUntil: moment(formData.expirationDate).format('YYYY-MM-DD'),
      customerNote: formData.overallNote
    };

    if (formData.quoteDisplay.setAsDefault) {
      await mutations.updateQuoteConfigurations({
        showManufacturerDetails
      });
    }

    const res = await mutations.updateQuote({
      id: quote?._id,
      ...body.previewInfo,
      ...mainBody
    });

    const type = quote?.type;

    captureDBAnalytics({
      eventType: DBEventType.QuotesModule,
      data: {
        ...(type === 'rfq' && { rfqId }),
        quoteId: quote?._id,
        action: 'updateQuoteDetails',
        ...(res?.error && { error: res?.error }),
        body: {
          ...body.previewInfo,
          ...mainBody
        },
        source: {
          name: type === 'rfq' ? 'RFQDetails' : 'QuoteDetails'
        }
      }
    });

    setMainLoading(false);
    if (!res) return;

    updateQuoteInfo({
      ...res,
      ...body,
      ...mainBody
    });
    setIsExpired(false);

    closeDialog(QUOTE_DETAILS_DIALOG);

    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    captureDBAnalytics,
    closeDialog,
    currentPreparedData,
    formData,
    onClose,
    quote?._id,
    rfqId,
    steps,
    mutations.updatePhoto,
    mutations.updateQuote,
    updateQuoteInfo,
    updateUser
  ]);

  const handleCancel = useCallback(() => {
    closeDialog(QUOTE_DETAILS_DIALOG);
    onClose();
  }, [closeDialog, onClose]);

  return {
    handleSave,
    handleCancel,
    mainLoading
  };
};
